import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject } from '@angular/core';

import { Observable, map, race, switchMap, tap, timer } from 'rxjs';

import { Banner, BannerLayoutType } from '@hdms/shared/common-types';
import { BannerHelperService, BannerService } from '@hdms/shared/data-access';
import { TranslationModule } from '@hdms/shared/feature';
import { CtaClickData, MarketingBannerComponent, ModalDismissReason, ModalModule } from '@hdms/shared/ui-core';
import { StripHtmlPipe } from '@hdms/shared/util-core';

const REDIRECT_TIMEOUT = 1000;

@Component({
  selector: 'shrd-pf-ft-marketing-banners',
  templateUrl: './marketing-banners.component.html',
  styleUrls: ['./marketing-banners.component.scss'],
  standalone: true,
  imports: [CommonModule, ModalModule, TranslationModule, MarketingBannerComponent, StripHtmlPipe],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarketingBannersComponent {
  opened = false;

  readonly bannerLayoutType = BannerLayoutType;

  private readonly bannerService = inject(BannerService);
  private readonly bannerHelperService = inject(BannerHelperService);

  content$ = this.getMarketingBanner$();

  onModalClose(dismissReason: ModalDismissReason, banner: Banner): void {
    this.opened = false;
    if (dismissReason !== ModalDismissReason.CLOSE_EXTERNAL) {
      this.temporarilyBanBanner(banner);
    }
  }

  temporarilyBanBanner(banner: Banner): void {
    this.bannerService.temporarilyBanMarketingBanner$(banner).subscribe();
  }

  permanentlyBanBanner(banner: Banner, trackingEnabled = true): void {
    this.bannerService.permanentlyBanMarketingBanner(banner, trackingEnabled);
  }

  /**
   * If CTA click is being handled, the actual anchor link redirect should be delayed
   * until temporaryBanBanner mutation has be requested successfully, otherwise
   * mutation request can become cancelled by subsequent page redirect.
   * Then an additional click will be triggered on the original CTA anchor tag.
   * @param event
   * @param data
   * @param banner
   */
  handleCtaClick({ event, data }: CtaClickData, banner: Banner): void {
    event.stopPropagation();
    event.preventDefault();

    this.bannerHelperService.trackBannerClick(banner, { promotion_cta: data.label });
    this.permanentlyBanBanner(banner, false);
    race(this.bannerService.temporarilyBanMarketingBanner$(banner, false), timer(REDIRECT_TIMEOUT)).subscribe(
      () => data.uri && (event.target as HTMLAnchorElement).click()
    );
  }

  private getMarketingBanner$(): Observable<Banner> {
    return this.bannerService.marketingBanner$.pipe(
      tap(_ => (this.opened = true)),
      switchMap(banner => timer(banner.displayDelay || 0).pipe(map(() => banner))),
      tap(banner => this.bannerHelperService.trackBannerImpression(banner))
    );
  }
}
